import React, { ComponentType, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { platformIntl, TRACKER } from '../../../../constants';
import { Button, Dropdown, Navigation, NavigationItemType, SvgIcon } from '@fx-ui/fine-design';
import { getMessageNavItemText, getMessageNavItemIcon } from '../../utils';
import { ThemedStateIcon } from '../../../../components';
import { compact, first, includes, isEmpty, map, size, SuiteId } from '@libs/shared';
import { SvgIconProps } from '@fx-ui/icon';
import useUnreadMessage from '../../../nav-message/hooks/useUnreadMessage';
import { tracker } from '../../../../utils';
import { authModel } from '../../../../models';
import SwitchIcon from '../../../../assets/svgicons/switch.svg';
import UnreadMessageStatus from '../unread-message-status';
import { MessageNavGroup, MessageNavType, SuiteUnreadStatus } from '../../interfaces';
import { checkSuiteAccessible } from '../../../../apis';

interface MessageCenterNavProps {
    className?: string;
    messageNavGroup?: MessageNavGroup;
    suiteUnreadStatus?: SuiteUnreadStatus[];
    currentSuiteId?: SuiteId;
    activeKey?: MessageNavType;
    showExternalMessage: boolean;
    hasUnreadExternalMessage: boolean;
    onActiveKeyChange: (key: MessageNavType) => void;
    onSuiteChange: (suiteId: SuiteId) => void;
}

interface MessageNavItem {
    key: MessageNavType | string;
    label?: React.ReactNode;
    type?: string;
    icon?: ComponentType<SvgIconProps>;
    activeIcon?: ComponentType<SvgIconProps>;
    children?: MessageNavItem[];
}

/**
 * 消息中心侧边导航栏
 */
const MessageCenterNav: React.FC<MessageCenterNavProps> = (props) => {
    const {
        className,
        messageNavGroup,
        suiteUnreadStatus,
        currentSuiteId,
        activeKey = '',
        showExternalMessage,
        hasUnreadExternalMessage,
        onSuiteChange,
        onActiveKeyChange
    } = props;
    const { unreadMessageStatus, initUnreadMessage } = useUnreadMessage();
    /**
     * 是否隐藏子产品切换按钮
     * 1. 在消息设置界面，不展示切换入口
     * 2. 当只有一个子产品时，不展示切换入口
     */
    const hideSuiteSwitcher =
        activeKey === MessageNavType.MessageSetting || size(suiteUnreadStatus) <= 1;

    const {
        suiteMessageNavItems,
        tenantMessageNavItems,
        operationMessageNavItems,
        messageTypes,
        unreadTypes
    } = useMemo(() => {
        const { suite = [], platform = [] } = messageNavGroup ?? {};
        // 子产品消息类型导航项
        const suiteMessageNavItems = map(suite, (item) => {
            return {
                key: item.key,
                label: item.label,
                ...getMessageNavItemIcon(item.key)
            };
        });
        // 企业消息类型导航项
        const tenantMessageNavItems = compact([
            ...map(platform, (item) => {
                return {
                    key: item.key,
                    label: item.label,
                    ...getMessageNavItemIcon(item.key)
                };
            }),
            // 外部消息由前端维护
            showExternalMessage && {
                key: MessageNavType.External,
                label: getMessageNavItemText(MessageNavType.External),
                ...getMessageNavItemIcon(MessageNavType.External)
            }
        ]);

        // 额外操作导航项
        const operationMessageNavItems = compact([
            authModel.hasSysAdmin && {
                key: MessageNavType.MessageSetting,
                label: getMessageNavItemText(MessageNavType.MessageSetting),
                ...getMessageNavItemIcon(MessageNavType.MessageSetting)
            }
        ]);

        const messageTypes = [
            ...map(suiteMessageNavItems, 'key'),
            ...map(tenantMessageNavItems, 'key'),
            ...map(operationMessageNavItems, 'key')
        ];

        /**
         * 外部消息和通知设置导航项不通过统一接口获取未读状态
         * 外部消息：未读状态通过单独接口获取（useExternalMessage）
         * 通知设置：不需要获取未读状态
         */
        const unreadTypes = messageTypes.filter(
            (type) => !includes([MessageNavType.External, MessageNavType.MessageSetting], type)
        );

        return {
            suiteMessageNavItems,
            tenantMessageNavItems,
            operationMessageNavItems,
            messageTypes,
            unreadTypes
        };
    }, [showExternalMessage, messageNavGroup]);

    const { suiteList, hasSuiteUnreadMessage, currentSuiteName } = useMemo(() => {
        let hasSuiteUnreadMessage = false;
        let currentSuiteName = '';
        const suiteList = map(suiteUnreadStatus, ({ suite_id, has_unread, suite_name }) => {
            const isCurrentSuite = suite_id === currentSuiteId;
            // 当前子产品不展示未读状态
            const showSuiteUnreadStatus = !isCurrentSuite && has_unread;
            // 除当前子产品外的任一其他子产有未读消息，那就在切换按钮右上角展示未读状态
            if (showSuiteUnreadStatus) {
                hasSuiteUnreadMessage = true;
            }
            const suiteName = suite_name;
            if (isCurrentSuite) {
                currentSuiteName = suiteName;
            }
            return {
                key: suite_id,
                onClick: () => {
                    if (isCurrentSuite) {
                        return;
                    }
                    checkSuiteAccessible(suite_id).then(() => {
                        onSuiteChange(suite_id);
                    });
                },
                className: classNames('suite-item', {
                    selected: isCurrentSuite
                }),
                label: (
                    <UnreadMessageStatus hasUnread={showSuiteUnreadStatus} dot offset={[3, -4]}>
                        <>{suiteName}</>
                    </UnreadMessageStatus>
                )
            };
        });

        return { suiteList, hasSuiteUnreadMessage, currentSuiteName };
    }, [suiteUnreadStatus, currentSuiteId]);

    useEffect(() => {
        initUnreadMessage(unreadTypes, currentSuiteId);
    }, [unreadTypes]);

    useEffect(() => {
        // 当切换子产品后，导航项改变时，如果切换后的导航项没有当前选中导航项，需要默认选中第一个有效的导航项
        if (!includes(messageTypes, activeKey)) {
            const firstValidType = first(messageTypes);
            if (firstValidType) {
                onActiveKeyChange(firstValidType);
            }
        }
    }, [messageTypes, activeKey]);

    const displaySuiteName = currentSuiteName;

    const { messageTypeNavItems, messageOperationNavItems } = useMemo(() => {
        const messageTypeNavItems: MessageNavItem[] = [
            {
                key: 'suite-message',
                label: (
                    <div className="suite-message-title">
                        {displaySuiteName}
                        {!hideSuiteSwitcher && (
                            <Dropdown
                                trigger="click"
                                className="fx-suite-list-dropdown"
                                menu={{
                                    items: suiteList,
                                    className: 'suite-list'
                                }}
                            >
                                <Button
                                    icon={
                                        <UnreadMessageStatus
                                            hasUnread={hasSuiteUnreadMessage}
                                            dot
                                            offset={[0, -3]}
                                        >
                                            <SvgIcon component={SwitchIcon} />
                                        </UnreadMessageStatus>
                                    }
                                    compact
                                    type="text"
                                    size="small"
                                >
                                    {platformIntl.get('fxp.base.workspace.msg.switch_prod.btn')}
                                </Button>
                            </Dropdown>
                        )}
                    </div>
                ),
                type: 'group',
                children: suiteMessageNavItems
            },
            {
                key: 'tenant-message',
                type: 'group',
                label: platformIntl.get('fxp.base.workspace.msg.corp_msg.ttl'),
                children: tenantMessageNavItems
            }
        ].filter((group) => !isEmpty(group.children));

        return {
            messageTypeNavItems: map(messageTypeNavItems, (group) => {
                return {
                    ...group,
                    children: map(group.children, (item) => {
                        const isActive = activeKey === item.key;
                        const hasUnread = (() => {
                            if (item.key === MessageNavType.External) {
                                return hasUnreadExternalMessage;
                            }
                            return Boolean(unreadMessageStatus[item.key]);
                        })();

                        return {
                            key: item.key,
                            label: item.label,
                            icon: (
                                <UnreadMessageStatus hasUnread={hasUnread} dot offset={[-1, -5]}>
                                    <ThemedStateIcon
                                        active={isActive}
                                        icon={item.icon}
                                        activeIcon={item.activeIcon}
                                    />
                                </UnreadMessageStatus>
                            )
                        };
                    })
                };
            }),
            messageOperationNavItems: map(operationMessageNavItems, (item) => {
                return {
                    ...item,
                    icon: (
                        <ThemedStateIcon
                            active={activeKey === item.key}
                            {...getMessageNavItemIcon(MessageNavType.MessageSetting)}
                        />
                    )
                };
            })
        };
    }, [
        activeKey,
        suiteList,
        hasSuiteUnreadMessage,
        displaySuiteName,
        unreadMessageStatus,
        suiteMessageNavItems,
        tenantMessageNavItems,
        operationMessageNavItems,
        hasUnreadExternalMessage
    ]);

    return (
        <div className={classNames('fx-message-center-nav', className)}>
            {!isEmpty(messageTypeNavItems) && (
                <Navigation
                    className="message-type-nav"
                    items={messageTypeNavItems as NavigationItemType[]}
                    selectedKeys={[activeKey]}
                    onClick={(info) => {
                        onActiveKeyChange(info.key as MessageNavType);
                        tracker(TRACKER.MESSAGE_CENTER_TAB_CLICK, info.key);
                    }}
                />
            )}
            {!isEmpty(messageOperationNavItems) && (
                <Navigation
                    className="message-operation-nav"
                    items={compact([
                        !isEmpty(messageTypeNavItems) && {
                            key: 'divider',
                            type: 'divider'
                        },
                        ...messageOperationNavItems
                    ])}
                    selectedKeys={[activeKey]}
                    onClick={(info) => {
                        onActiveKeyChange(info.key as MessageNavType);
                        tracker(TRACKER.MESSAGE_CENTER_TAB_CLICK, info.key);
                    }}
                />
            )}
        </div>
    );
};

export default MessageCenterNav;
